import axios from "axios";
import { filterParams } from "@/context/FilterContext";
import { loginPath, tenantHeaders } from "@/lib/tenant";
import { embedSlug, embedToken, isEmbed } from "@/lib/embed";

export const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

api.interceptors.request.use((cfg) => {
  cfg.headers = { ...(cfg.headers || {}), ...tenantHeaders() };
  if (isEmbed()) {
    cfg.headers.Authorization = `Bearer ${embedToken()}`;
    cfg.withCredentials = false;
    delete cfg.headers["X-Tenant"];
    if (embedSlug()) cfg.headers["X-Tenant-Slug"] = embedSlug(); else delete cfg.headers["X-Tenant-Slug"];
  }
  if (!cfg.url.startsWith("/auth/") && !cfg.url.startsWith("/settings/")) cfg.params = { ...filterParams(), ...(cfg.params || {}) };
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && isEmbed()) {
      window.dispatchEvent(new Event("crm-embed-expired"));
      return Promise.reject(error);
    }
    if (error.response?.status === 401 && !original._retry && !original.url.includes("/auth/") && !window.location.pathname.startsWith("/embed")) {
      original._retry = true;
      try {
        await api.post("/auth/refresh");
        return api(original);
      } catch (e) {
        window.location.href = loginPath();
      }
    }
    return Promise.reject(error);
  }
);

export function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (d == null) {
    const st = e?.response?.status;
    if (st && (st >= 502 || e?.response?.data instanceof Blob || typeof e?.response?.data === "string")) return "Il server non ha risposto in tempo. L'elaborazione continua in background: riprova tra qualche secondo.";
    if (e?.code === "ECONNABORTED") return "Richiesta troppo lunga: riprova tra qualche secondo.";
    return e?.message || "Si è verificato un errore. Riprova.";
  }
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return d.msg || String(d);
}

export const pollUntilReady = (url, onData, onError, isAlive, intervalMs = 5000, maxMs = 6 * 60 * 1000, params = {}) => {
  let first = true;
  const t0 = Date.now();
  const tick = () => api.get(url, { params: { ...params, wait: first ? 4 : 20 } }).then((r) => {
    if (!isAlive()) return;
    if (r.data?.status === "computing") {
      onData(null, true);
      if (Date.now() - t0 > maxMs) return onError("L'analisi sta richiedendo più del previsto. Il calcolo continua sul server: premi Riprova tra qualche minuto.");
      first = false;
      setTimeout(tick, intervalMs);
    } else onData(r.data, false);
  }).catch((e) => { if (isAlive()) onError(errMsg(e)); });
  tick();
};

export const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "");

const eurFmt = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", useGrouping: "always" });
const numFmt = new Intl.NumberFormat("it-IT", { useGrouping: "always" });
export const notifySettings = () => window.dispatchEvent(new Event("crm-settings-changed"));
export const eur = (v) => eurFmt.format(v || 0);
export const num = (v) => numFmt.format(v || 0);
export const fdate = (s) => (s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");
export const fdatetime = (s) => (s ? new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
